<template>
  <!-- Hero -->
  <section class="hero" aria-labelledby="page-title">
    <div class="section__inner hero__inner">
      <h2 id="page-title">Contact</h2>
    </div>
  </section>

  <main class="page-content">
    <section class="inquiry-wrap" aria-label="상담 문의 상세">
      <nav class="crumbs" aria-label="breadcrumb">
        <ol>
          <li>Contact</li>
          <li aria-current="page">상세</li>
        </ol>
      </nav>

      <!-- 상태 표시(선택) -->
      <div class="meta" v-if="state.questionId">
        <span class="badge" v-if="state.createdAt">작성일: {{ state.createdAt.slice(0,16) }}</span>
      </div>

      <!-- 로딩/에러 -->
      <p v-if="loading" class="notice">불러오는 중...</p>
      <p v-else-if="errorMsg" class="error" role="alert">{{ errorMsg }}</p>

      <!-- 상세 내용 -->
      <dl v-else class="detail-grid" aria-label="문의 상세 내용">
        <!-- Row 1 -->
        <dt class="label">문의분야</dt>
        <dd class="field col-span-3">
          <div class="value-box">
            <span class="value-text">{{ state.category || "-" }}</span>
          </div>
        </dd>

        <!-- Row 2 -->
        <dt class="label">
          성명 <span class="req" aria-hidden="true">*</span>
          <span class="sr-only">필수</span>
        </dt>
        <dd class="field col-span-3">
          <div class="value-box">
            <span class="value-text">{{ state.userName || "-" }}</span>
          </div>
        </dd>

        <!-- Row 3 -->
        <dt class="label">
          연락처 <span class="req" aria-hidden="true">*</span>
          <span class="sr-only">필수</span>
        </dt>
        <dd class="field col-span-1">
          <div class="value-box">
            <span class="value-text">{{ state.phoneNumber || "-" }}</span>
          </div>
        </dd>

        <dt class="label label-right">e-mail</dt>
        <dd class="field col-span-1">
          <div class="value-box">
            <span class="value-text">{{ state.email || "-" }}</span>
          </div>
        </dd>

        <!-- Row 4 -->
        <dt class="label">제목</dt>
        <dd class="field col-span-3">
          <div class="value-box">
            <span class="value-text">{{ state.title || "-" }}</span>
          </div>
        </dd>

        <!-- Row 5 -->
        <dt class="label">내용</dt>
        <dd class="field col-span-3">
          <div class="value-box value-box--content">
            <!-- 줄바꿈 보존 -->
            <div class="value-pre">{{ state.content || "-" }}</div>
          </div>
        </dd>

        <!-- ✅ Answer Section (조회 전용, 리스트) -->
        <dt class="label">답변</dt>
        <dd class="field col-span-3">
          <div class="answer-box">
            <div class="answer-head">
              <div class="answer-title">
                <strong>관리자 답변</strong>
                <span v-if="answers.length" class="answer-badge answer-badge--done">
                  <!-- {{ answers.length }}건 -->
                </span>
                <span v-else class="answer-badge answer-badge--wait">답변 대기</span>
              </div>
            </div>

            <!-- 답변이 있을 때 -->
            <div v-if="answers.length" class="answer-list">
              <article v-for="a in answers" :key="a.answerId" class="answer-item">
                <div class="answer-item__meta">
                  <span class="answer-item__writer">{{ a.adminName }}</span>
                  <span class="answer-item__date" v-if="a.createdAt">{{ a.createdAt.slice(0,16) }}</span>
                </div>
                <div class="answer-pre">{{ a.content }}</div>
              </article>
            </div>

            <!-- 답변이 없을 때 -->
            <div v-else class="answer-empty">
              아직 등록된 답변이 없습니다.
            </div>
          </div>
        </dd>

        <!-- Actions -->
        <div class="actions col-span-4">
          <button type="button" class="btn btn-secondary" @click="handleCancel">목록</button>
        </div>
      </dl>
    </section>
  </main>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { qnaDetail, answerList  } from "@/api/qna";
import * as Utils from "@/common/util/util";

const router = useRouter();
const route = useRoute();
const store = useStore();

const loading = ref(false);
const errorMsg = ref("");

const userInfo = computed(() => store.state.temp?.userInfo || null);
const userId = computed(() => userInfo.value?.userId || null);

const answers = ref([]); // [{ answerId, adminName, content, createdAt, ... }]

const state = reactive({
  questionId: "",
  category: "",
  userName: "",
  phoneNumber: "",
  email: "",
  title: "",
  content: "",
  status: "",
  createdAt: "",
  updatedAt: "",
});

const getQuestionIdFromRoute = () => route.params.questionId || route.query.questionId || "";

const fetchDetail = async () => {
  const qid = getQuestionIdFromRoute();

  if (Utils.isEmpty(qid)) {
    errorMsg.value = "questionId가 없습니다. (라우트 파라미터/쿼리를 확인하세요)";
    return;
  }

  loading.value = true;
  errorMsg.value = "";

  try {
    const resultVo = await qnaDetail({ questionId: qid, userId: userId.value });

    if (resultVo?.result !== "ok") {
      errorMsg.value = resultVo?.resultMsg ?? "상세 조회에 실패했습니다.";
      return;
    }

    const d = resultVo?.data || {};

    state.questionId = d.questionId ?? "";
    state.category = d.category ?? "";
    state.userName = d.userName ?? "";
    state.phoneNumber = d.phoneNumber ?? "";
    state.email = d.email ?? "";
    state.title = d.title ?? "";
    state.content = d.content ?? "";
    state.status = d.status ?? "";
    state.createdAt = d.createdAt ?? "";
    state.updatedAt = d.updatedAt ?? "";

    // 2) ✅ 관리자 답변 목록
    const answerVo = await answerList({ questionId: qid });
    if (answerVo?.result === "ok" && Array.isArray(answerVo?.data)) {
      // content가 관리자 답변 본문
      answers.value = answerVo.data
        .filter((row) => row?.content) // 내용 없는 것 제외
        .map((row) => ({
          answerId: row.answerId ?? "",
          adminId: row.adminId ?? "",
          adminName: row.adminName ?? "관리자",
          content: row.content ?? "",
          createdAt: row.createdAt ?? "",
          updatedAt: row.updatedAt ?? "",
        }));
    } else {
      answers.value = [];
    }

  } catch (e) {
    console.error("qnaDetail 오류:", e);
    errorMsg.value = "상세 조회 중 오류가 발생했습니다.";
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  router.push({ name: "qnamain" });
};

onMounted(fetchDetail);
</script>

<style scoped>
.inquiry-wrap,
.inquiry-wrap * {
  font-family: 'Noto Sans KR', sans-serif;
}

.page-title {
  font-size: 1.625rem;
  font-weight: 700;
  text-align: center;
  padding: 14px 0 18px;
}

p { font-size: 0.95rem; }

.page-content { width: 100%; }

.inquiry-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px 40px;
}

/* ===== 브레드크럼 ===== */
.crumbs ol {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 24px 0 10px;
  font-size: 0.92rem;
  color: #64748b;
}
.crumbs li + li::before {
  content: "/";
  margin-right: 8px;
  color: #64748b;
}

/* ===== Hero ===== */
.hero {
  background:
    linear-gradient(rgba(13, 6, 20, .6), rgba(13, 6, 20, .6)),
    url('/src/assets/traingMain.jpeg') center 45% / 65% no-repeat;
  color: #fff;
}
.hero__inner { padding: 140px 16px; text-align: center; }
.hero h2 { font-weight: 800; font-size: 42px; letter-spacing: -0.5px; }
@media (max-width: 600px) {
  .hero__inner { padding: 80px 16px; }
  .hero h2 { font-size: 28px; }
}

/* 메타 */
.meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 10px 0 14px;
}
.badge {
  border: 1px solid #dcdcdc;
  background: #fff;
  padding: 6px 10px;
  font-size: 0.9rem;
  color: #334155;
}

/* ===== Detail Grid (DL) ===== */
.detail-grid {
  display: grid;
  grid-template-columns: 160px 1fr 160px 1fr;
  gap: 14px 16px;
  border-top: 1px solid #e6e6e6;
  padding-top: 18px;
  margin: 0;
}

.label {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #222;
  font-weight: 600;
  padding-left: 10px;
}
.label-right { justify-content: center; text-align: center; }

.req { color: #d11; margin-left: 4px; }

.field { margin: 0; }

.col-span-3 { grid-column: span 3; }
.col-span-4 { grid-column: span 4; }

/* 값 표시 박스 */
.value-box {
  width: 100%;
  border: 1px solid #dcdcdc;
  background: #fff;
  padding: 10px 12px;
  font-size: 0.95rem;
  box-sizing: border-box;
}
.value-text { color: #0f172a; }
.value-box--content { min-height: 240px; }
.value-pre {
  white-space: pre-wrap;  /* 줄바꿈/공백 유지 */
  word-break: break-word; /* 긴 텍스트 줄바꿈 */
  font-size: 0.95rem;
  line-height: 1.6;
}

/* 버튼 */
.actions {
  display: grid;
  grid-template-columns: auto;
  justify-content: center;
  gap: 12px;
  margin-top: 10px;
}
.btn {
  min-width: 160px;
  padding: 12px 14px;
  font-weight: 700;
  border: 1px solid #bdbdbd;
  cursor: pointer;
}
.btn-secondary {
  background: #9a9a9a;
  color: #fff;
  border-color: #9a9a9a;
}

.notice { color: #334155; }
.error { color: #d11; font-weight: 600; }

@media (max-width: 900px) {
  .detail-grid { grid-template-columns: 120px 1fr; }
  .label-right { justify-content: flex-start; text-align: left; }
  .col-span-3, .col-span-4 { grid-column: span 1; }
}

.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

.answer-box {
  border: 1px solid #dcdcdc;
  background: #fff;
  padding: 12px;
  box-sizing: border-box;
}

.answer-head {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  align-items: start;
  margin-bottom: 10px;
}

.answer-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #0f172a;
}

/* .answer-badge {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  font-size: 0.85rem;
  padding: 3px 8px;
}

.answer-badge--wait {
  border-color: #e2e8f0;
  background: #f1f5f9;
  color: #64748b;
}

.answer-badge--done {
  border-color: #67e8f9;
  background: #ecfeff;
  color: #0369a1;
} */

.answer-list {
  display: grid;
  gap: 10px;
}

.answer-item {
  border-top: 1px solid #eef2f7;
  padding-top: 10px;
}

.answer-item__meta {
  display: flex;
  gap: 10px;
  align-items: baseline;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 0.9rem;
}

.answer-item__writer {
  font-weight: 700;
  color: #0f172a;
}

.answer-pre {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
  font-size: 0.95rem;
  color: #0f172a;
}

.answer-empty {
  color: #64748b;
  font-size: 0.95rem;
}

</style>
